// Street geometry shared by OutsideScene and every scene that returns to the street.
// Phaser-free so it loads under `node --test`. The street is ONE baked image
// (bg_street.webp, 8358×941 = [gas station 1671][dealer+school 1671][old street 5016])
// scaled to height 720; world x = srcX * STREET_SCALE.
import { CHARACTER_HALF_H, centreYFromGround } from './ui/carCatalog.mjs';

export const STREET_SRC_W = 8358;
export const STREET_SRC_H = 941;
export const STREET_SCALE = 720 / STREET_SRC_H;          // 0.76514
export const STREET_WORLD_WIDTH = Math.round(STREET_SRC_W * STREET_SCALE);  // 6395
// Old-street srcX values were measured on the 5016px artwork; it now starts here.
export const OLD_STREET_SRC_OFFSET = 3342;

export function streetX(srcX) { return Math.round(srcX * STREET_SCALE); }

// The panorama is served as tiles (scripts/split_street.py — TILES/OVERLAP must match): a
// phone GPU caps a texture at 8192 px (older ones 4096) and one over the limit uploads as
// nothing — the whole street drew pure black on phones. Each tile repeats OVERLAP px of the
// next one on its right edge so drawing them in order shows no seam. Source px; world = ×STREET_SCALE.
export const STREET_TILE_COUNT = 4;
export const STREET_TILE_OVERLAP = 2;
export const STREET_TILES = Array.from({ length: STREET_TILE_COUNT }, (_, i) => {
  const base = Math.floor(STREET_SRC_W / STREET_TILE_COUNT);
  const srcX = i * base;
  const srcEnd = i === STREET_TILE_COUNT - 1 ? STREET_SRC_W : Math.min(STREET_SRC_W, (i + 1) * base + STREET_TILE_OVERLAP);
  return { srcX, srcW: srcEnd - srcX };
});
// Each look of the street has its own four tiles: street/streetLooks.mjs streetTileKeys(look).

// Door centres, left→right. `key` doubles as the STREET_SPAWNS key.
export const STREET_DOORS = [
  { key: 'CAR_DEALER',     srcX: 1991,                          label: 'Salon samochodowy' },
  { key: 'DRIVING_SCHOOL', srcX: 2781,                          label: 'Szkoła jazdy' },
  { key: 'GAME_HALL',      srcX: 478  + OLD_STREET_SRC_OFFSET,  label: 'Salon Gier' },
  { key: 'BIKE_SHOP',      srcX: 1122 + OLD_STREET_SRC_OFFSET,  label: 'Rowery' },
  { key: 'FURNITURE',      srcX: 2000 + OLD_STREET_SRC_OFFSET,  label: 'Meble' },
  { key: 'CLOTHING',       srcX: 2790 + OLD_STREET_SRC_OFFSET,  label: 'Ubrania' },
  { key: 'PET',            srcX: 3715 + OLD_STREET_SRC_OFFSET,  label: 'Zwierzęta' },
  { key: 'SHOP',           srcX: 4490 + OLD_STREET_SRC_OFFSET,  label: 'Warzywniak' },
].map(d => ({ ...d, x: streetX(d.srcX) }));

// --- where the ground is ----------------------------------------------------------------
//
// Read off bg_street.webp on 2026-08-30 (PIL, a brightness profile down 130 columns of the
// 8358×941 art, converted to world y with STREET_SCALE). Three bands, the same all the way
// along the street:
//
//   world y 491 … 526   the pavement in front of the shops (bright, warm grey)
//   world y 528 … 643   the asphalt (flat mid grey), kerb line at 526-527
//   world y 644 …       the grass verge with the flowers
//
// People walk on the PAVEMENT — the walking line used to run along the grass BELOW the road,
// which is why parked cars sat half on the lawn and the child crossed in front of traffic to
// reach a shop door. Cars stand on the asphalt (see ROAD_WHEEL_Y in ui/carCatalog.mjs).
export const STREET_SIDEWALK_TOP_Y = 491;    // where the pavement starts (the shop fronts)
export const STREET_SIDEWALK_KERB_Y = 526;   // …and where it drops to the road
export const STREET_ASPHALT_TOP_Y = 528;
export const STREET_ASPHALT_BOTTOM_Y = 643;
// Below the asphalt a light kerb strip (world y 645–659) runs along the grass verge, which
// starts at 660 (profiled 2026-09-07). Feet may stand on the strip — its far edge is where a
// character stops before the grass.
export const STREET_KERB_FRONT_Y = 656;

// The FEET line: how far up and down a character may walk — from the shopfronts, across the
// pavement and the whole road, down to the grass verge (the flower bed below stays off
// limits) — and where she is put when she has no better idea (a spawn out of a shop, a
// lesson she just abandoned): a few px inside the pavement so she never straddles the kerb
// or the shop thresholds. Cars and bikes share the road with her; she grows with the
// perspective as she comes down it.
export const STREET_FEET = { backY: 496, frontY: STREET_KERB_FRONT_Y };
export const STREET_PAVEMENT_FEET_FRONT_Y = 522;   // the pavement's own front edge
export const STREET_FEET_Y = 518;

// How far up and down a BIKE may go: the same band the walker uses (pavement + road down to
// the grass verge). Wheel line, like STREET_FEET is a feet line.
export const STREET_RIDE = { backY: STREET_FEET.backY, frontY: STREET_KERB_FRONT_Y };

// Where a character stands on the street: the sprite-CENTRE y a scene should spawn her at.
// The street draws people at OUTDOOR_CHARACTER_SCALE (a 1.2 m child is 120 px, not 240) and
// then applies the y-perspective on top (see carCatalog's PERSPECTIVE) — so the centre is
// CHARACTER_HALF_H above her feet at the reference plane, a shade more below it.
export const STREET_SPAWN_Y = centreYFromGround(CHARACTER_HALF_H, STREET_FEET_Y);   // 458
export const STREET_WALK_CENTER_Y = {
  min: centreYFromGround(CHARACTER_HALF_H, STREET_FEET.backY),
  max: centreYFromGround(CHARACTER_HALF_H, STREET_FEET.frontY),
};

export const STREET_SPAWNS = Object.freeze({
  ...Object.fromEntries(STREET_DOORS.map(d => [d.key, d.x])),
  GAS_STATION: streetX(830),        // kiosk centre — decorative, used as a landmark
  HOUSE: 3638 + 2557,               // where MainScene drops you on the street (right end)
});
